/**
 * Split a stored address into postal lines for mailing surfaces (envelopes,
 * statement "To" blocks).
 *
 * Addresses in this app are sometimes stored multi-line (one segment per line)
 * and sometimes as a single comma-joined string — e.g. QuickBooks/CINC imports
 * land as `"Parkside Village Condominiums, 3242 Parkview Circle, Grove City, OH 43123"`.
 * A single long line is wrong on an envelope, so when there are no embedded
 * newlines we split on commas and recombine the trailing `City, ST ZIP` into one
 * line per US postal convention:
 *
 *   Parkside Village Condominiums
 *   3242 Parkview Circle
 *   Grove City, OH 43123
 *
 * Already-multiline addresses are returned as-is (trimmed, blanks dropped).
 */
const CITY_STATE_ZIP = /^[A-Za-z]{2}\s+\d{5}(-\d{4})?$/;

export const splitPostalAddress = (raw: string | undefined | null): string[] => {
	const s = (raw ?? '').trim();
	if (!s) return [];
	if (/\r?\n/.test(s)) return s.split(/\r?\n/).map((t) => t.trim()).filter(Boolean);

	const parts = s.split(',').map((t) => t.trim()).filter(Boolean);
	if (parts.length <= 1) return parts;

	// Combine a trailing "ST 12345" with the preceding city segment into the
	// canonical "City, ST 12345" last line.
	const last = parts[parts.length - 1];
	if (parts.length >= 2 && CITY_STATE_ZIP.test(last)) {
		const city = parts[parts.length - 2];
		return [...parts.slice(0, parts.length - 2), `${city}, ${last}`];
	}
	return parts;
};

/**
 * The mailing columns an account carries; structural so this module stays free of model imports.
 * `null` is accepted because some report rows (A/R aging) carry their address as `string | null`.
 */
export type MailingAddressFields = {
	address?: string | null;
	mailingAddress?: string | null;
	mailingCareOf?: string | null;
	mailingCity?: string | null;
	mailingState?: string | null;
	mailingZip?: string | null;
};

/**
 * Compose an account's MAILING address into postal lines — the single composer
 * behind both the printed envelope (`recipientFor`) and the account page's
 * on-screen mailing block, so the two can never disagree about where a
 * homeowner's mail actually goes.
 *
 * Returns `[]` when no free-text `mailingAddress` is stored, even if the
 * city/state/zip columns are populated: a "City, ST ZIP" with no street isn't a
 * deliverable address, and the envelope falls back to the unit address in that
 * case. Display must show the same thing the envelope would print.
 */
export const mailingAddressLines = (a: MailingAddressFields): string[] => {
	if (!a.mailingAddress?.trim()) return [];
	const lines = splitPostalAddress(a.mailingAddress);
	const zip = a.mailingZip ?? '';
	const cityLine = [[a.mailingCity, a.mailingState].filter(Boolean).join(', '), zip]
		.filter(Boolean).join(' ').trim();
	// Only append a composed city/state/zip line when it isn't already part of
	// the free-text mailing address (avoids a duplicate last line) — imports
	// routinely land the whole address, city/zip included, in one field.
	if (cityLine && zip && !lines.some((l) => l.includes(zip))) lines.push(cityLine);
	return lines;
};

export interface RecipientAddress {
	/** The `mailing_care_of` column, printed as "c/o X" — unset when the address text already opens with it. */
	coLine?: string;
	addressLines: string[];
}

/**
 * The address block every printed "To" prints — envelope, statement, invoice. The
 * mailing address (city/state/zip columns folded in) when there is a street, else
 * the unit address.
 *
 * One composer on purpose: each surface once read `mailingAddress` alone, so a city
 * line kept in its own columns never printed and the mail was undeliverable.
 */
export const recipientAddress = (a: MailingAddressFields): RecipientAddress => {
	const mailing = mailingAddressLines(a);
	const addressLines = mailing.length > 0 ? mailing : splitPostalAddress(a.address);
	const careOf = a.mailingCareOf?.trim();
	const inText = !!careOf && collapse(addressLines[0] ?? '') === collapse(`c/o ${careOf}`);
	return { coLine: careOf && !inText ? careOf : undefined, addressLines };
};

/**
 * One normalizer, used for BOTH "are these the same address" and "does this
 * query appear in this address".
 *
 * Commas collapse along with whitespace. They have to: the same city can be
 * stored as a comma-joined tail of `mailing_address` or as its own
 * `mailing_city` column, and a user typing "lakeside oh" shouldn't match one
 * and miss the other purely because of where an importer put the comma.
 */
const collapse = (s: string): string => s.toLowerCase().replace(/[\s,]+/g, ' ').trim();

const normalizeForCompare = (lines: string[]): string => collapse(lines.join('\n'));

/**
 * Every address an account can be FOUND by: the unit address and the mailing
 * address, as two independent haystacks.
 *
 * Two, not one concatenated blob, on purpose — a single joined string lets a
 * query match across the seam between them ("43440 1290") and return an account
 * whose address contains neither phrase.
 *
 * Both sides are `collapse`d, so a stored
 * `"1510 North Buck Road; Lot #1\nLakeside, OH 43440"` still matches a typed
 * `"Lot #1 Lakeside"` — the line break and comma are storage artefacts, not
 * something the user can see to type around.
 *
 * The mailing side is the DISPLAYED block (`mailingBlockLines`), not the raw
 * columns. Every surface that matches with this shows that block, so a match on
 * anything else — a mailing city/state/zip with no street — returns a row
 * containing none of the words typed (#549).
 */
export const accountAddressHaystacks = (a: MailingAddressFields): string[] =>
	[a.address ?? '', mailingBlockLines(a).join(' ')].map(collapse).filter(Boolean);

/** True when `query` appears in the account's unit OR mailing address. Empty query matches. */
export const matchesAccountAddress = (a: MailingAddressFields, query: string): boolean => {
	const needle = collapse(query);
	if (!needle) return true;
	return accountAddressHaystacks(a).some((h) => h.includes(needle));
};

/**
 * The mailing block to show on an account screen, or `[]` when there is nothing
 * worth showing.
 *
 * Mirrors what `recipientFor` would actually put on an envelope, because a
 * screen that disagrees with the envelope about where someone's mail goes is
 * worse than a screen that says nothing:
 *
 *  - A `c/o` ALWAYS shows, over whichever address the envelope would use —
 *    mailing when there's a street, else the unit address. `recipientFor` emits
 *    `coLine` independently of `addressLines`, so "mail it to the unit,
 *    attention the family trust" is a real addressing instruction that exists
 *    with no mailing street of its own.
 *  - Without a `c/o`, the mailing address shows only when it DIFFERS from the
 *    unit address (ignoring case, commas and line breaks). Roster imports
 *    routinely copy one column into the other, and a second identical block
 *    reads as though the owner has two addresses.
 *
 * City/state/zip with no street stays invisible here — `mailingAddressLines`
 * drops it and the envelope falls back to the unit address, so showing it would
 * be the screen inventing an address nothing will ever mail to.
 */
export const mailingBlockLines = (a: MailingAddressFields): string[] => {
	const careOf = a.mailingCareOf?.trim();
	const mailing = mailingAddressLines(a);
	if (careOf) return [`c/o ${careOf}`, ...(mailing.length > 0 ? mailing : splitPostalAddress(a.address))];
	if (mailing.length === 0) return [];
	if (normalizeForCompare(mailing) === normalizeForCompare(splitPostalAddress(a.address))) return [];
	return mailing;
};
